/**
 * CopyFundraiserModal.tsx - Fundraiser Duplication Modal
 *
 * This component provides an interface for duplicating existing fundraisers
 * with options to customize the new fundraiser's basic information.
 *
 * Key Features:
 * - Fundraiser duplication with customizable fields
 * - Name and title editing for the new fundraiser
 * - Validation to prevent duplicate names
 * - Progress indication during copy operation
 * - Success confirmation and navigation options
 *
 * Copy Process:
 * - Duplicates all fundraiser settings and configuration
 * - Resets donation amounts and statistics
 * - Preserves design, layout, and content settings
 * - Creates new unique ID for the copied fundraiser
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 *
 * Dependencies: MobX store, portal rendering, form validation
 */

import React, { useState } from "react";
import styled from "styled-components";
import { NavigateFunction } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { X } from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { firstValueFrom } from "rxjs";
import { FundraiserService } from "../services";
import { AuthService, withRouter } from "@mindmemobile/mm-shared-global";
import ToastService from "./ToastService";

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalHeader = styled.div`
  padding: 32px 32px 24px;
  text-align: center;
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const ModalSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
`;

const FormSection = styled.div`
  padding: 0 32px 32px;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const ModalButton = styled.button<{ variant?: "primary" }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${(props) =>
    props.variant === "primary"
      ? `
    background: #3b82f6;
    color: #ffffff;

    &:hover {
      background: #2563eb;
    }

    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  `
      : `
    background: #f1f5f9;
    color: #64748b;

    &:hover {
      background: #e2e8f0;
    }
  `}
`;

interface CopyFundraiserModalProps {
  isVisible: boolean;
  onClose: (refresh?: boolean) => void;
  navigate: NavigateFunction;
  name: string;
  fundraiserId: string;
}

const CopyFundraiserModal: React.FC<CopyFundraiserModalProps> = observer(
  ({ isVisible, onClose, navigate, name, fundraiserId }) => {
    const [fundraiserName, setFundraiserName] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);

    // Set default name when modal opens
    React.useEffect(() => {
      if (isVisible && name) {
        setFundraiserName(`${name} (Copy)`);
      }
    }, [isVisible, name]);

    const handleOverlayClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    React.useEffect(() => {
      if (isVisible) {
        document.addEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "unset";
      }

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "unset";
      };
    }, [isVisible]);

    const handleCopy = async () => {
      if (fundraiserId && fundraiserName.trim()) {
        setIsProcessing(true);
        const response = await firstValueFrom(
          FundraiserService.copyFundraiser(
            AuthService.authData.accountId,
            fundraiserId,
            AuthService.mindmeAccountDetails?.userId || "",
            fundraiserName.trim()
          )
        );
        if (response.result.id) {
          onClose(true);
          ToastService.showSuccess("Fundraiser copied successfully");
          // fundraiserStore.setView("dashboard");
          navigate("/fundraiser/dashboard");
        } else {
          ToastService.showError("Failed to copy fundraiser");
        }
      }
      setIsProcessing(false);
    };

    const handleCancel = () => {
      onClose();
      if (name) {
        setFundraiserName(`${name} (Copy)`);
      }
    };

    const isFormValid = fundraiserName.trim().length > 0;

    return (
      <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
        <ModalContent>
          <CloseButton onClick={() => onClose()}>
            <X size={20} />
          </CloseButton>

          <ModalHeader>
            <ModalTitle>Copy Fundraiser</ModalTitle>
            <ModalSubtitle>
              Create a copy of this fundraiser with a new name
            </ModalSubtitle>
          </ModalHeader>

          <FormSection>
            <FormGroup>
              <FormLabel htmlFor="fundraiserName">Fundraiser Name</FormLabel>
              <FormInput
                id="fundraiserName"
                type="text"
                value={fundraiserName}
                onChange={(e) => setFundraiserName(e.target.value)}
                placeholder="Enter fundraiser name"
                autoFocus
              />
            </FormGroup>

            <ModalActions>
              <ModalButton onClick={handleCancel}>Cancel</ModalButton>
              <ModalButton
                variant="primary"
                onClick={handleCopy}
                disabled={!isFormValid || isProcessing}
              >
                Copy
              </ModalButton>
            </ModalActions>
          </FormSection>
        </ModalContent>
      </ModalOverlay>
    );
  }
);

export default withRouter(CopyFundraiserModal);
